import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { supabase } from '../../config/supabase';
import { Sentry } from '../../config/sentry';

type Step = 'loading' | 'otp' | 'onboarding' | 'invalid' | 'already-logged-in-wrong-email';

interface InvitePreview {
  space_name: string;
  role: string;
  email: string;
}

const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `${local[0]}*@${domain}`;
  return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
};

const roleLabel = (role: string) => role === 'editor' ? 'Can edit' : 'Can view';

export default function InviteAccept() {
  const token = new URLSearchParams(window.location.search).get('token');

  const [step, setStep] = useState<Step>('loading');
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [otp, setOtp] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const otpSentRef = useRef(false);

  useEffect(() => {
    if (!token) { setStep('invalid'); return; }

    const init = async () => {
      // Fetch invite preview (public, no auth needed)
      const { data, error: previewError } = await supabase.rpc('get_invitation_preview', { p_token: token });
      if (previewError || !data || !data.valid) {
        setStep('invalid');
        return;
      }
      setInvite(data as InvitePreview);

      // Check if already authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        if (session.user.email?.toLowerCase() === data.email.toLowerCase()) {
          // Correct user — accept immediately
          await acceptAndRedirect(session.user.email!);
          return;
        } else {
          // Wrong account logged in
          setStep('already-logged-in-wrong-email');
          return;
        }
      }

      // Not logged in — send OTP and show code input
      if (!otpSentRef.current) {
        otpSentRef.current = true;
        await supabase.auth.signInWithOtp({
          email: data.email,
          options: { shouldCreateUser: true },
        });
      }
      setStep('otp');
    };

    init();
  }, [token]);

  const acceptAndRedirect = async (email: string) => {
    const { data, error: rpcError } = await supabase.rpc('accept_invitation', { p_token: token });
    if (rpcError || !data?.success) {
      Sentry.captureException(rpcError || new Error(data?.error), { tags: { action: 'accept_invitation' } });
      setError(data?.error || rpcError?.message || 'Failed to accept invitation. Please try again.');
      return;
    }
    window.location.href = '/app';
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite) return;
    setLoading(true);
    setError('');
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      email: invite.email,
      token: otp,
      type: 'email',
    });
    if (verifyError) {
      setError(verifyError.message);
      setLoading(false);
      return;
    }
    // New user: no display_name set yet
    const hasName = !!data.user?.user_metadata?.display_name;
    if (!hasName) {
      setStep('onboarding');
      setLoading(false);
      return;
    }
    await acceptAndRedirect(invite.email);
    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (!invite) return;
    setError('');
    await supabase.auth.signInWithOtp({
      email: invite.email,
      options: { shouldCreateUser: true },
    });
  };

  const handleOnboarding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite || !displayName.trim()) return;
    setLoading(true);
    setError('');
    const { error: updateError } = await supabase.auth.updateUser({
      data: { display_name: displayName.trim() },
    });
    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }
    await acceptAndRedirect(invite.email);
    setLoading(false);
  };

  const inputClass = 'w-full px-3 py-2 border border-[var(--input-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] bg-[var(--input-bg)] text-[var(--content-primary)] placeholder-[var(--content-tertiary)]';

  return (
    <div className="min-h-screen bg-[var(--surface-secondary)] flex items-center justify-center p-4"
      style={{ '--accent-primary': '#2a9d8f', '--accent-hover': '#238578' } as React.CSSProperties}>
      <div className="bg-[var(--surface-primary)] rounded-2xl shadow-xl p-8 w-full max-w-md border border-[var(--border-primary)]">
        <div className="flex flex-col items-center mb-8">
          <Logo size="lg" className="mb-4" />
        </div>

        {step === 'loading' && (
          <p className="text-center text-sm text-[var(--content-secondary)]">Loading invitation…</p>
        )}

        {step === 'invalid' && (
          <div className="text-center space-y-4">
            <p className="text-sm font-medium text-[var(--content-primary)]">Invitation not found or expired</p>
            <p className="text-sm text-[var(--content-secondary)]">
              This invite link is invalid or has already been used. Ask the space owner to send a new invitation.
            </p>
            <a href="/app" className="inline-block text-sm text-[var(--accent-primary)] hover:underline">
              Go to Mozayc
            </a>
          </div>
        )}

        {step === 'already-logged-in-wrong-email' && invite && (
          <div className="text-center space-y-4">
            <p className="text-sm font-medium text-[var(--content-primary)]">Wrong account</p>
            <p className="text-sm text-[var(--content-secondary)]">
              This invitation was sent to <strong>{maskEmail(invite.email)}</strong>, but you're signed in
              with a different account. Sign out and open this link again.
            </p>
            <button
              onClick={async () => { await supabase.auth.signOut(); window.location.reload(); }}
              className="text-sm text-[var(--accent-primary)] hover:underline"
            >
              Sign out
            </button>
          </div>
        )}

        {step === 'otp' && invite && (
          <>
            <div className="mb-6 text-center">
              <p className="text-[15px] font-semibold text-[var(--content-primary)] mb-1">
                Join <span className="text-[var(--accent-primary)]">{invite.space_name}</span>
              </p>
              <p className="text-sm text-[var(--content-secondary)]">
                You've been invited to join as <strong>{roleLabel(invite.role)}</strong>.
              </p>
            </div>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  className="w-full px-3 py-3 border border-[var(--input-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] bg-[var(--input-bg)] text-[var(--content-primary)] text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  autoFocus
                  maxLength={6}
                  required
                  onFocus={(e) => e.target.setSelectionRange(0, 0)}
                />
                <p className="text-xs text-[var(--content-tertiary)] mt-2 text-center">
                  We sent a code to <strong>{maskEmail(invite.email)}</strong>.
                  You can also click the link in the email directly.
                </p>
              </div>
              {error && (
                <div className="bg-[var(--status-error-bg)] text-[var(--error)] px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-hover)] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying…' : 'Continue'}
              </button>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={loading}
                className="w-full text-center text-sm text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors disabled:opacity-50"
              >
                Resend code
              </button>
            </form>
          </>
        )}

        {step === 'onboarding' && invite && (
          <>
            <div className="mb-6 text-center">
              <p className="text-[15px] font-semibold text-[var(--content-primary)] mb-1">Welcome to Mozayc</p>
              <p className="text-sm text-[var(--content-secondary)]">
                Choose a display name to get started.
              </p>
            </div>
            <form onSubmit={handleOnboarding} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--content-primary)] mb-1">
                  Your name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => { setDisplayName(e.target.value); setError(''); }}
                  className={inputClass}
                  placeholder="e.g. Alex Smith"
                  autoFocus
                  required
                />
              </div>
              {error && (
                <div className="bg-[var(--status-error-bg)] text-[var(--error)] px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !displayName.trim()}
                className="w-full bg-[var(--accent-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--accent-hover)] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Getting started…' : 'Get started'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
